import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CommonService } from './common.service';

@Injectable({
    providedIn: 'root'
})
export class QueryStringService {
  private mutations: MutationObserver;
  private dynamicParams: Record<string, string> = {};

    constructor(@Inject(DOCUMENT) private dom: Document, private commonService: CommonService) {
        this.dom.location.search;
    }

  getParameter(name: string, persistCasing: boolean = false, url?: string) {
		if (!url) url = this.dom.location.href || this.dom.URL;
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)', 'i');
    const results = persistCasing ? regex.exec(url) : regex.exec(url.toLowerCase());

		if (!results) return null;
		if (!results[2]) return '';
		return decodeURIComponent(results[2].replace(/\+/g, ' '));
	}

  getFullQspString(): string {
    const existingURLSearchParams = new URLSearchParams(this.dom.location.search);
    let existingQueryParams = Object.fromEntries(existingURLSearchParams.entries());
    existingQueryParams = this.commonService.removeBlackListedParams(existingQueryParams);
    const sanitizedParams = this.commonService.sanitizeParams(existingQueryParams);
    return new URLSearchParams(sanitizedParams).toString();
  }

  /**
   * Creates a mutation observer and listens for chat widget load
   */
  appendQspOnCookieBanner() {
    const cookieBanner = this.dom.getElementById('onetrust-banner-sdk');
    if (cookieBanner) {
      this.appendQspToLinks();
      return;
    }
    this.mutations = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        if (mutation.type === "childList" && mutation.addedNodes?.length>0) {
          mutation.addedNodes.forEach((node: HTMLElement| any) => {
            try {
              if(node instanceof HTMLElement && node.id == 'onetrust-consent-sdk') {
                this.appendQspToLinks();
                // Stops listening for mutation events.
                this.mutations.disconnect();
              }
            } catch (error) {
              console.error('Error while appending QSP to cookie banner links:', error);
            }
          })
        }
      });
    });

    // Starts listening to the mutation created above.
    this.mutations.observe(this.dom.body, {
      childList: true,
      characterData: true
    });
  }

  private appendQspToLinks() {
    const qsp = this.getFullQspString();
    this.dom.querySelectorAll('#onetrust-banner-sdk a').forEach((link: HTMLAnchorElement) => {
      const href = new URL(link.href, this.dom.baseURI || this.dom.location.origin);
      qsp.split('&').forEach(param => {
        const [key, value] = param.split('=');
        if (key) {
          href.searchParams.set(key, value ?? '');
        }
      });
      link.setAttribute('href', href.toString());
    });
  }

  appendQueryStringParams(url: string): string {
    if (!url) return '';
  
    const qsp = this.getFullQspString();
    if (!qsp) return url;
  
    // Check if URL already has query params
    const separator = url.includes('?') ? '&' : '?';
  
    return `${url}${separator}${qsp}`;
  }

  getDynamicParams(): Record<string, string> {
    return { ...this.dynamicParams };
  }

  clearDynamicParams(): void {
    if (!this.commonService.isPlatformBrowser()) return;
    const keys = Object.keys(this.dynamicParams);
    if (keys.length === 0) return;
    keys.forEach(key => this.updateApplyNowHref(key));
  }

  updateApplyNowHref(key: string, value?: string): void {
    if (!value && !this.dynamicParams[key]) return;
    if (value) {
      this.dynamicParams[key] = value;
    } else {
      delete this.dynamicParams[key];
    }
    if (!this.commonService.isPlatformBrowser()) return;
    this.dom.querySelectorAll('a[href*="/enrollment-application"]').forEach((el: Element) => {
      const anchor = el as HTMLAnchorElement;
      const href = anchor.getAttribute('href');
      if (!href) return;
      const url = new URL(href, this.dom.baseURI || this.dom.location.origin);
      if (value) {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
      anchor.setAttribute('href', url.toString());
    });
  }

  setApplyNowLink(internalUrl?: string): string {
    if (!internalUrl) return '';
    return internalUrl.startsWith('/') ? internalUrl : `/${internalUrl}`;
  }
}
